import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PublicRoute from './components/Auth/PublicRoute';
import DashboardLayout from './components/Layout/DashboardLayout';

// Landing Pages (rotas ainda no SPA; home/sobre/legal são HTML estático)
import LandingAllFeatures from './pages/Landing/AllFeatures';
import LandingContact from './pages/Landing/Contact';
import LandingHelpCenter from './pages/Landing/HelpCenter';
import LandingNotFound from './pages/Landing/NotFound';

// Auth Pages
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import WelcomeNewUserPage from './pages/Auth/WelcomeNewUserPage';

// Dashboard Pages
import WelcomeView from './components/Welcome/WelcomeView';
import WordAnalyzer from './pages/agents/WordAnalyzer';
import SermonBuilder from './pages/agents/SermonBuilder';
import BibleStudyCreator from './pages/agents/BibleStudyCreator';
import CharacterAnalyzer from './pages/agents/CharacterAnalyzer';
import TranslationComparator from './pages/agents/TranslationComparator';
import LiteraryFormIdentifier from './pages/agents/LiteraryFormIdentifier';
import ContextResearcher from './pages/agents/ContextResearcher';
import TheologyPresenter from './pages/agents/TheologyPresenter';
import CharacterBiographer from './pages/agents/CharacterBiographer';
import TheologyDescriber from './pages/agents/TheologyDescriber';
import ThemeExplorer from './pages/agents/ThemeExplorer';
import TextRelator from './pages/agents/TextRelator';
import TextUpdater from './pages/agents/TextUpdater';
import WorldviewAnalyzer from './pages/agents/WorldviewAnalyzer';
import InterpretationHistorian from './pages/agents/InterpretationHistorian';
import TargetAudienceDefiner from './pages/agents/TargetAudienceDefiner';
import IllustrationGenerator from './pages/agents/IllustrationGenerator';
import LessonPreparer from './pages/agents/LessonPreparer';
import PostCreator from './pages/agents/PostCreator';
import PersonalDevotional from './pages/agents/PersonalDevotional';
import UnitIdentifier from './pages/agents/UnitIdentifier';
import ProfilePage from './pages/Profile/ProfilePage';
import SupportMessagesPage from './pages/SupportMessages/SupportMessagesPage';

const queryClient = new QueryClient();

const legacyDashboardRedirects: Array<{ from: string; to: string }> = [
  { from: "/dashboard", to: "/app/dashboard" },
  { from: "/profile", to: "/app/profile" },
  { from: "/support-messages", to: "/app/support-messages" },
  { from: "/agents/word-analyzer", to: "/app/agents/word-analyzer" },
  { from: "/agents/sermon-builder", to: "/app/agents/sermon-builder" },
  { from: "/agents/bible-study-creator", to: "/app/agents/bible-study-creator" },
  { from: "/agents/character-analyzer", to: "/app/agents/character-analyzer" },
  { from: "/agents/translation-comparator", to: "/app/agents/translation-comparator" },
  { from: "/agents/literary-form-identifier", to: "/app/agents/literary-form-identifier" },
  { from: "/agents/context-researcher", to: "/app/agents/context-researcher" },
  { from: "/agents/theology-presenter", to: "/app/agents/theology-presenter" },
  { from: "/agents/character-biographer", to: "/app/agents/character-biographer" },
  { from: "/agents/theology-describer", to: "/app/agents/theology-describer" },
  { from: "/agents/theme-explorer", to: "/app/agents/theme-explorer" },
  { from: "/agents/text-relator", to: "/app/agents/text-relator" },
  { from: "/agents/text-updater", to: "/app/agents/text-updater" },
  { from: "/agents/worldview-analyzer", to: "/app/agents/worldview-analyzer" },
  { from: "/agents/interpretation-historian", to: "/app/agents/interpretation-historian" },
  { from: "/agents/target-audience-definer", to: "/app/agents/target-audience-definer" },
  { from: "/agents/illustration-generator", to: "/app/agents/illustration-generator" },
  { from: "/agents/lesson-preparer", to: "/app/agents/lesson-preparer" },
  { from: "/agents/post-creator", to: "/app/agents/post-creator" },
  { from: "/agents/personal-devotional", to: "/app/agents/personal-devotional" },
];

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AuthProvider>
            <Router>
              <ScrollToTop />
              <Routes>
          {/* Landing SPA leftovers (home/sobre/compromissos/legal = HTML estático em public/) */}
          <Route path="/recursos" element={<LandingAllFeatures />} />
          <Route path="/contato" element={<LandingContact />} />
          <Route path="/ajuda" element={<LandingHelpCenter />} />

          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/verify-email" 
            element={
              <PublicRoute>
                <VerifyEmailPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/reset-password" 
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/welcome-new-user" 
            element={
              <PublicRoute>
                <WelcomeNewUserPage />
              </PublicRoute>
            } 
          />

          {/* Protected Routes */}
          <Route 
            path="/app" 
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<WelcomeView />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="support-messages" element={<SupportMessagesPage />} />
            
            {/* Agent Routes */}
            <Route path="agents/word-analyzer" element={<WordAnalyzer />} />
            <Route path="agents/sermon-builder" element={<SermonBuilder />} />
            <Route path="agents/bible-study-creator" element={<BibleStudyCreator />} />
            <Route path="agents/character-analyzer" element={<CharacterAnalyzer />} />
            <Route path="agents/translation-comparator" element={<TranslationComparator />} />
            <Route path="agents/literary-form-identifier" element={<LiteraryFormIdentifier />} />
            <Route path="agents/context-researcher" element={<ContextResearcher />} />
            <Route path="agents/theology-presenter" element={<TheologyPresenter />} />
            <Route path="agents/character-biographer" element={<CharacterBiographer />} />
            <Route path="agents/theology-describer" element={<TheologyDescriber />} />
            <Route path="agents/theme-explorer" element={<ThemeExplorer />} />
            <Route path="agents/text-relator" element={<TextRelator />} />
            <Route path="agents/text-updater" element={<TextUpdater />} />
            <Route path="agents/worldview-analyzer" element={<WorldviewAnalyzer />} />
            <Route path="agents/interpretation-historian" element={<InterpretationHistorian />} />
            <Route path="agents/target-audience-definer" element={<TargetAudienceDefiner />} />
            <Route path="agents/illustration-generator" element={<IllustrationGenerator />} />
            <Route path="agents/lesson-preparer" element={<LessonPreparer />} />
            <Route path="agents/post-creator" element={<PostCreator />} />
            <Route path="agents/personal-devotional" element={<PersonalDevotional />} />
            <Route path="agents/unit-identifier" element={<UnitIdentifier />} />
            <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
          </Route>

          {legacyDashboardRedirects.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}

          {/* Catch all route */}
          <Route path="*" element={<LandingNotFound />} />
              </Routes>
            </Router>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;